import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Phone, 
  Copy, 
  MessageSquare,
  Clock,
  AlertTriangle,
  CheckCircle,
  Volume2,
  VolumeX
} from 'lucide-react';
import toast from 'react-hot-toast';

interface CallModalProps {
  isOpen: boolean;
  onClose: () => void;
  facility: {
    name: string;
    contact: string;
    type: string;
    status: string;
  };
}

const CallModal: React.FC<CallModalProps> = ({ isOpen, onClose, facility }) => {
  const [isDialing, setIsDialing] = useState(false);
  const [callConfirmed, setCallConfirmed] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);

  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const handleCall = () => {
    if (isMobile) {
      // Mobile device - use tel: protocol
      setIsDialing(true);
      setTimeout(() => {
        window.location.href = `tel:${facility.contact}`;
        setIsDialing(false);
        setCallConfirmed(true);
        toast.success('Opening phone dialer...');
        
        // Auto-close modal after successful call initiation
        setTimeout(() => {
          onClose();
        }, 2000);
      }, 1000);
    } else {
      // Desktop - show number and copy option
      copyPhoneNumber();
      setCallConfirmed(true);
    }
  };

  const copyPhoneNumber = () => {
    navigator.clipboard.writeText(facility.contact);
    toast.success('Phone number copied to clipboard');
  };

  const sendSMS = () => {
    if (isMobile) {
      const message = encodeURIComponent(`Hello, I need assistance at ${facility.name}. This is an emergency contact through DisasterConnect.`);
      window.location.href = `sms:${facility.contact}?body=${message}`;
      toast.success('Opening SMS app...');
    } else {
      toast.error('SMS is only available on mobile devices');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-accent-green';
      case 'full': return 'text-accent-orange';
      case 'closed': return 'text-accent-red';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return CheckCircle;
      case 'full': return Clock;
      case 'closed': return AlertTriangle;
      default: return AlertTriangle;
    }
  };

  const formatPhoneNumber = (phone: string) => {
    // Format phone number for display
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="bg-dark-800 border border-dark-600 rounded-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent-green rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Contact Facility</h2>
                  <p className="text-sm text-gray-400">Emergency communication</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Facility Info */}
            <div className="p-6 border-b border-dark-700">
              <h3 className="font-semibold text-white mb-2">{facility.name}</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-300 capitalize">{facility.type}</span>
                </div>
                <div className={`flex items-center space-x-1 ${getStatusColor(facility.status)}`}>
                  {React.createElement(getStatusIcon(facility.status), { className: "w-4 h-4" })}
                  <span className="text-sm font-medium capitalize">{facility.status}</span>
                </div>
              </div>
            </div>

            {/* Phone Number Display */}
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-white mb-2">
                  {formatPhoneNumber(facility.contact)}
                </div>
                <p className="text-sm text-gray-400">
                  {isMobile ? 'Tap to call' : 'Click to copy number'}
                </p>
              </div>

              {/* Call Status */}
              {isDialing && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center mb-4"
                >
                  <div className="w-8 h-8 border-2 border-accent-green border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-accent-green text-sm">Connecting call...</p>
                </motion.div>
              )}

              {callConfirmed && !isDialing && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center mb-4"
                >
                  <CheckCircle className="w-8 h-8 text-accent-green mx-auto mb-2" />
                  <p className="text-accent-green text-sm">
                    {isMobile ? 'Call initiated successfully' : 'Number copied to clipboard'}
                  </p>
                </motion.div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCall}
                  disabled={isDialing}
                  className="w-full bg-gradient-to-r from-accent-green to-primary-400 py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Phone className="w-5 h-5" />
                  {isMobile ? 'Call Now' : 'Copy Number'}
                </motion.button>

                {isMobile && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={sendSMS}
                    className="w-full border border-dark-600 py-3 rounded-lg font-semibold text-gray-300 hover:bg-dark-700 transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <MessageSquare className="w-5 h-5" />
                    Send SMS
                  </motion.button>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={copyPhoneNumber}
                  className="w-full border border-dark-600 py-3 rounded-lg font-semibold text-gray-300 hover:bg-dark-700 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Copy className="w-5 h-5" />
                  Copy Number
                </motion.button>
              </div>

              {/* Emergency Notice */}
              <div className="mt-6 p-3 bg-accent-red/10 border border-accent-red/30 rounded-lg">
                <div className="flex items-center space-x-2 text-accent-red">
                  <AlertTriangle className="w-4 h-4" />
                  <p className="text-xs">
                    For life-threatening emergencies, call 911 immediately
                  </p>
                </div>
              </div>

              {/* Call Quality Options (Mobile Only) */}
              {isMobile && callConfirmed && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 bg-dark-700/50 rounded-lg"
                >
                  <h4 className="text-sm font-medium text-white mb-2">Call Options</h4>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Speaker</span>
                    <button
                      onClick={() => setIsSpeakerOn(!isSpeakerOn)}
                      className={`p-2 rounded-lg transition-colors ${
                        isSpeakerOn ? 'bg-primary-400 text-white' : 'bg-dark-600 text-gray-400'
                      }`}
                    >
                      {isSpeakerOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CallModal;